import { apiFetch, ApiError } from "@/lib/api";

export type ItemCategory = string;

export type Condition =
  | "New with tags" | "Excellent" | "Good" | "Fair" | "Needs repair" | "Archive";

export type AcquisitionType =
  | "Owned" | "On loan" | "Gifted" | "Pulled for consideration" | "Archive piece";

export type Season = "SS" | "FW" | "Resort" | "Pre-Fall" | "Seasonless";

export type LocationType =
  | "Rack" | "Bin" | "Archive" | "Client residence"
  | "Storage unit" | "On set" | "Other";

export type MoveReason =
  | "Pull for fitting" | "Shoot prep" | "Return"
  | "Storage rotation" | "Repair" | "Other";

export type Occasion =
  | "Press" | "Red carpet" | "Editorial" | "Fitting" | "Tour"
  | "Daywear" | "Evening" | "Resort" | "Other";

export type OutfitRole =
  | "Hero" | "Layer" | "Accessory" | "Shoes" | "Bag" | "Jewelry" | "Other";

export const OUTFIT_ROLES: OutfitRole[] = [
  "Hero", "Layer", "Accessory", "Shoes", "Bag", "Jewelry", "Other",
];

/** Display order on the detail page. Matches backend `ROLE_ORDER`. */
export const OUTFIT_ROLE_ORDER: OutfitRole[] = [
  "Hero", "Layer", "Accessory", "Shoes", "Bag", "Jewelry", "Other",
];

/** Mirror of backend `infer_role()` — keep in sync. */
export function inferOutfitRole(category?: string | null): OutfitRole {
  switch (category) {
    case "Shoes": return "Shoes";
    case "Bags": return "Bag";
    case "Jewelry": return "Jewelry";
    case "Accessories": return "Accessory";
    case "Outerwear":
    case "Knitwear":
    case "Suiting": return "Layer";
    case "Dresses":
    case "Tops":
    case "Bottoms": return "Hero";
    default: return "Other";
  }
}

export type LoanStatus = "Active" | "Returned" | "Lost";
export type LoanEffectiveStatus = LoanStatus | "Overdue";
export type BorrowerType = "Client" | "Talent" | "Shoot" | "Stylist" | "Other";
export type ReturnCondition =
  | "As sent" | "Minor wear" | "Needs cleaning" | "Needs repair" | "Damaged";

export type ShareExpiry = "7d" | "30d" | "90d" | "never";

export interface Photo {
  id: string;
  url: string;
  is_primary: boolean;
  uploaded_at: string;
}

export type ItemKind = "clothing" | "shoes" | "accessory";
export interface MaterialEntry { name: string; percent?: number | null; }

export interface Item {
  _id: string;
  workspace_id: string;
  name: string;
  brand?: string | null;
  category: ItemCategory;
  sub_category?: string | null;
  size?: string | null;
  color?: string | null;
  secondary_colors: string[];
  material?: string | null;
  materials?: MaterialEntry[];
  fabric_type?: string | null;
  fabric_features?: string[];
  item_kind?: ItemKind | null;
  size_system?: string | null;
  shoe_type?: string | null;
  color_families?: string[];
  color_legacy?: string | null;
  condition: Condition;
  condition_notes?: string | null;
  acquisition_type: AcquisitionType;
  acquisition_date?: string | null;
  retail_value?: number | null;
  insured_value?: number | null;
  client_id?: string | null;
  current_location_id?: string | null;
  current_location_name?: string | null;
  location_type?: string | null;
  tags: string[];
  season?: Season | null;
  sku?: string | null;
  box?: string | null;
  internal_code: string;
  photos: Photo[];
  notes?: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface ClientRecord {
  _id: string;
  workspace_id: string;
  name: string;
  pronouns?: string | null;
  email?: string | null;
  phone?: string | null;
  measurements: Record<string, string | undefined>;
  preferred_brands: string[];
  notes?: string | null;
  photo_url?: string | null;
  archived: boolean;
  item_count: number;
  created_at: string;
  updated_at: string;
}

export interface LocationRecord {
  _id: string;
  workspace_id: string;
  name: string;
  type: LocationType;
  address?: string | null;
  notes?: string | null;
  archived: boolean;
  item_count: number;
  created_at: string;
  updated_at: string;
}

export interface FacetBucket { value: string; count: number }

export interface ItemListResponse {
  items: Item[];
  total: number;
  page: number;
  page_size: number;
  facets: Record<string, FacetBucket[]>;
}

export interface Movement {
  _id: string;
  workspace_id: string;
  item_id: string;
  from_location_id?: string | null;
  to_location_id?: string | null;
  moved_by: string;
  moved_at: string;
  reason?: string | null;
  notes?: string | null;
}

export interface DashboardSummary {
  total_items: number;
  items_out: number;
  inventory_value: number;
  clients_count: number;
  locations_count: number;
  active_loans: number;
  overdue_loans: number;
  outfits_count: number;
}

export interface RecentItem {
  id: string;
  name: string;
  brand?: string | null;
  category?: string | null;
  primary_photo_url?: string | null;
  created_at: string;
}

export interface ActivityEntry {
  id: string;
  item_id: string;
  item_name?: string | null;
  from_location_id?: string | null;
  from_location_name?: string | null;
  to_location_id?: string | null;
  to_location_name?: string | null;
  moved_at: string;
  moved_by?: string | null;
  moved_by_name?: string | null;
  reason?: string | null;
}

export interface OutfitItemThumb {
  item_id: string;
  name: string;
  brand?: string | null;
  category?: string | null;
  primary_photo_url?: string | null;
  role: OutfitRole;
  notes?: string | null;
}

export interface OutfitItemEntryInput {
  item_id: string;
  role?: OutfitRole;
  notes?: string;
}

export interface Outfit {
  _id: string;
  workspace_id: string;
  name: string;
  description?: string | null;
  client_id?: string | null;
  occasion?: Occasion | null;
  scheduled_for?: string | null;
  tags: string[];
  item_ids: string[];
  items: OutfitItemThumb[];
  cover_photo_url?: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface LoanItemThumb {
  id: string;
  name: string;
  brand?: string | null;
  primary_photo_url?: string | null;
  internal_code?: string | null;
}

export interface Loan {
  _id: string;
  workspace_id: string;
  item_id: string;
  item?: LoanItemThumb | null;
  borrower_name: string;
  borrower_email?: string | null;
  borrower_type: BorrowerType;
  purpose?: string | null;
  picked_up_at: string;
  due_at: string;
  status: LoanStatus;
  effective_status: LoanEffectiveStatus;
  is_overdue: boolean;
  returned_at?: string | null;
  return_condition?: ReturnCondition | null;
  return_notes?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface LoanSummary {
  active: number;
  overdue: number;
  returned_total: number;
}

export interface SharedLink {
  _id: string;
  workspace_id: string;
  kind: "outfit" | "workspace";
  target_id: string;
  token: string;
  title?: string | null;
  expires_at?: string | null;
  revoked: boolean;
  view_count: number;
  has_password?: boolean;
  show_values?: boolean;
  created_at: string;
  created_by: string;
}

export interface GroupedItem {
  id: string; name: string; brand?: string | null; category?: string | null;
  primary_photo_url?: string | null; box?: string | null; location_type?: string | null;
}
export interface GroupedGroup {
  key: string; label: string; sub?: string | null; count: number; items: GroupedItem[];
}

export interface PortalItem {
  name: string; brand?: string | null; category?: string | null;
  primary_photo_url?: string | null; location_name?: string | null;
  location_type?: string | null; box?: string | null; condition?: string | null;
  insured_value?: number | null; retail_value?: number | null;
}
export interface PortalItemsResponse {
  items: PortalItem[]; total: number; page: number; page_size: number;
  show_values: boolean;
  filters: { categories?: string[]; location_types?: string[]; locations?: { id: string; name: string }[] };
}

export interface PublicShareItem {
  name: string;
  brand?: string | null;
  category?: string | null;
  primary_photo_url?: string | null;
}

export interface PublicShareResponse {
  title: string;
  kind: "outfit";
  workspace_name: string;
  workspace_logo_url?: string | null;
  description?: string | null;
  occasion?: string | null;
  items: PublicShareItem[];
  created_at: string;
}

export interface ItemFilters {
  q?: string;
  brand?: string[];
  category?: string[];
  condition?: string[];
  season?: string[];
  size?: string[];
  color?: string;
  color_family?: string[];
  material?: string[];
  shoe_type?: string[];
  client_id?: string;
  location_id?: string;
  location_type?: string[];
  tags?: string[];
  archived?: boolean;
  sort?: "created_desc" | "created_asc" | "name_asc" | "name_desc" | "brand_asc" | "brand_desc" | "value_desc" | "value_asc";
  page?: number;
  page_size?: number;
}

function toQuery(filters: ItemFilters): string {
  const sp = new URLSearchParams();
  if (filters.q) sp.set("q", filters.q);
  filters.brand?.forEach((b) => sp.append("brand", b));
  filters.category?.forEach((c) => sp.append("category", c));
  filters.condition?.forEach((c) => sp.append("condition", c));
  filters.season?.forEach((s) => sp.append("season", s));
  filters.location_type?.forEach((l) => sp.append("location_type", l));
  filters.tags?.forEach((t) => sp.append("tags", t));
  filters.size?.forEach((v) => sp.append("size", v));
  filters.color_family?.forEach((v) => sp.append("color_family", v));
  filters.material?.forEach((v) => sp.append("material", v));
  filters.shoe_type?.forEach((v) => sp.append("shoe_type", v));
  if (filters.color) sp.set("color", filters.color);
  if (filters.client_id) sp.set("client_id", filters.client_id);
  if (filters.location_id) sp.set("location_id", filters.location_id);
  if (typeof filters.archived === "boolean") sp.set("archived", String(filters.archived));
  if (filters.sort) sp.set("sort", filters.sort);
  if (filters.page) sp.set("page", String(filters.page));
  if (filters.page_size) sp.set("page_size", String(filters.page_size));
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const itemsApi = {
  list: (filters: ItemFilters = {}) =>
    apiFetch<ItemListResponse>(`/items${toQuery(filters)}`),
  get: (id: string) => apiFetch<Item>(`/items/${id}`),
  byCode: (code: string) =>
    apiFetch<{ id: string; name: string; internal_code: string }>(
      `/items/by-code/${encodeURIComponent(code)}`
    ),
  create: (body: Partial<Item>) =>
    apiFetch<Item>("/items", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, patch: Partial<Item>) =>
    apiFetch<Item>(`/items/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  archive: (id: string) =>
    apiFetch<Item>(`/items/${id}`, { method: "DELETE" }),
  move: (id: string, body: { to_location_id: string; reason?: string; notes?: string }) =>
    apiFetch<Item>(`/items/${id}/move`, { method: "POST", body: JSON.stringify(body) }),
  bulkMove: (body: {
    item_ids: string[]; to_location_id: string;
    reason?: string; notes?: string;
  }) =>
    apiFetch<{ moved: number; skipped: number; missing: string[] }>(
      "/items/bulk-move",
      { method: "POST", body: JSON.stringify(body) }
    ),
  bulkDelete: (item_ids: string[]) =>
    apiFetch<{ deleted: number }>("/items/bulk-delete",
      { method: "POST", body: JSON.stringify({ item_ids }) }),
  bulkEdit: (item_ids: string[], field: string, value: string | null) =>
    apiFetch<{ updated: number }>("/items/bulk-edit",
      { method: "POST", body: JSON.stringify({ item_ids, field, value }) }),
  grouped: (filters: ItemFilters = {}, by: string = "location") => {
    const qs = toQuery({ ...filters, page: undefined, page_size: undefined } as ItemFilters);
    const sep = qs ? "&" : "?";
    return apiFetch<{ groups: GroupedGroup[]; total: number }>(
      `/items/grouped${qs}${sep}by=${by}`);
  },
  allIds: (filters: ItemFilters = {}) =>
    apiFetch<{ ids: string[]; total: number }>(`/items/all-ids${toQuery({ ...filters, page: undefined, page_size: undefined } as ItemFilters)}`),
  movements: (id: string) => apiFetch<Movement[]>(`/items/${id}/movements`),
  uploadPhoto: async (id: string, file: File): Promise<Item> => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<Item>(`/items/${id}/photos`, { method: "POST", body: fd });
  },
  deletePhoto: (id: string, photoId: string) =>
    apiFetch<Item>(`/items/${id}/photos/${photoId}`, { method: "DELETE" }),
  setPrimaryPhoto: (id: string, photoId: string) =>
    apiFetch<Item>(`/items/${id}/photos/${photoId}/primary`, { method: "POST" }),
};

export const taxonomyApi = {
  categories: () => apiFetch<{ categories: string[] }>("/taxonomy/categories"),
  addCategory: (name: string) =>
    apiFetch<{ categories: string[] }>("/taxonomy/categories", {
      method: "POST", body: JSON.stringify({ name }),
    }),
  brands: () => apiFetch<{ brands: string[] }>("/taxonomy/brands"),
  addBrand: (name: string) =>
    apiFetch<{ brands: string[] }>("/taxonomy/brands", {
      method: "POST", body: JSON.stringify({ name }),
    }),
  locationTypes: () => apiFetch<{ location_types: string[] }>("/taxonomy/location-types"),
  addLocationType: (name: string) =>
    apiFetch<{ location_types: string[] }>("/taxonomy/location-types", {
      method: "POST", body: JSON.stringify({ name }),
    }),
};

export interface WorkspaceSummary { id: string; name: string; }
export const workspacesApi = {
  list: () => apiFetch<{ workspaces: WorkspaceSummary[]; current_workspace_id: string }>("/workspaces"),
  create: (name: string) =>
    apiFetch<WorkspaceSummary>("/workspaces", { method: "POST", body: JSON.stringify({ name }) }),
  switch: (id: string) =>
    apiFetch<{ ok: boolean; workspace_id: string }>(`/workspaces/${id}/switch`, { method: "POST" }),
};

export const clientsApi = {
  list: (q?: string) => apiFetch<ClientRecord[]>(`/clients${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  get: (id: string) => apiFetch<ClientRecord>(`/clients/${id}`),
  create: (body: Partial<ClientRecord>) =>
    apiFetch<ClientRecord>("/clients", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, patch: Partial<ClientRecord>) =>
    apiFetch<ClientRecord>(`/clients/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  archive: (id: string) =>
    apiFetch<ClientRecord>(`/clients/${id}`, { method: "DELETE" }),
  items: (id: string) =>
    apiFetch<{ items: Item[]; total: number }>(`/clients/${id}/items`),
};

export const locationsApi = {
  list: () => apiFetch<LocationRecord[]>("/locations"),
  get: (id: string) => apiFetch<LocationRecord>(`/locations/${id}`),
  create: (body: Partial<LocationRecord>) =>
    apiFetch<LocationRecord>("/locations", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, patch: Partial<LocationRecord>) =>
    apiFetch<LocationRecord>(`/locations/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  archive: (id: string) =>
    apiFetch<LocationRecord>(`/locations/${id}`, { method: "DELETE" }),
  items: (id: string) =>
    apiFetch<{ items: Item[]; total: number }>(`/locations/${id}/items`),
};

export const dashboardApi = {
  summary: () => apiFetch<DashboardSummary>("/dashboard/summary"),
  recentItems: () => apiFetch<RecentItem[]>("/dashboard/recent-items"),
  activity: () => apiFetch<ActivityEntry[]>("/dashboard/activity"),
};

export const outfitsApi = {
  list: (params?: { archived?: boolean; client_id?: string; q?: string }) => {
    const sp = new URLSearchParams();
    if (params?.archived) sp.set("archived", "true");
    if (params?.client_id) sp.set("client_id", params.client_id);
    if (params?.q) sp.set("q", params.q);
    const s = sp.toString();
    return apiFetch<Outfit[]>(`/outfits${s ? `?${s}` : ""}`);
  },
  get: (id: string) => apiFetch<Outfit>(`/outfits/${id}`),
  create: (body: Partial<Outfit> & { item_entries?: OutfitItemEntryInput[]; item_ids?: string[] }) =>
    apiFetch<Outfit>("/outfits", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, patch: Partial<Outfit> & { item_entries?: OutfitItemEntryInput[]; item_ids?: string[] }) =>
    apiFetch<Outfit>(`/outfits/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  archive: (id: string) =>
    apiFetch<Outfit>(`/outfits/${id}`, { method: "DELETE" }),
};

export const loansApi = {
  list: (params?: { status?: LoanStatus; overdue?: boolean; item_id?: string }) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set("status", params.status);
    if (params?.overdue) sp.set("overdue", "true");
    if (params?.item_id) sp.set("item_id", params.item_id);
    const s = sp.toString();
    return apiFetch<Loan[]>(`/loans${s ? `?${s}` : ""}`);
  },
  summary: () => apiFetch<LoanSummary>("/loans/summary"),
  get: (id: string) => apiFetch<Loan>(`/loans/${id}`),
  create: (body: {
    item_id: string;
    borrower_name: string;
    borrower_email?: string;
    borrower_type?: BorrowerType;
    purpose?: string;
    picked_up_at?: string;
    due_at: string;
    notes?: string;
  }) =>
    apiFetch<Loan>("/loans", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, patch: Partial<Loan>) =>
    apiFetch<Loan>(`/loans/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  return: (id: string, body: {
    return_location_id?: string;
    return_condition?: ReturnCondition;
    return_notes?: string;
  }) =>
    apiFetch<Loan>(`/loans/${id}/return`, { method: "POST", body: JSON.stringify(body) }),
  lost: (id: string) =>
    apiFetch<Loan>(`/loans/${id}/lost`, { method: "POST" }),
};

export const sharesApi = {
  list: () => apiFetch<SharedLink[]>("/shares"),
  create: (body: { kind: "outfit" | "workspace"; target_id?: string; title?: string; expiry?: ShareExpiry; password?: string; show_values?: boolean }) =>
    apiFetch<SharedLink>("/shares", { method: "POST", body: JSON.stringify(body) }),
  revoke: (id: string) =>
    apiFetch<SharedLink>(`/shares/${id}`, { method: "DELETE" }),
  // public — no auth, do NOT pass auth header
  publicView: (token: string) =>
    apiFetch<PublicShareResponse>(`/public/shares/${encodeURIComponent(token)}`, {
      auth: false,
    }),
};

export const portalApi = {
  info: (token: string) =>
    apiFetch<{ requires_password: boolean; workspace_name: string; workspace_logo_url?: string | null; title?: string | null }>(
      `/public/shares/portal/${encodeURIComponent(token)}/info`, { auth: false }),
  items: (token: string, body: { password?: string; q?: string; category?: string; location_id?: string; location_type?: string; page?: number; page_size?: number }) =>
    apiFetch<PortalItemsResponse>(`/public/shares/portal/${encodeURIComponent(token)}/items`, {
      method: "POST", body: JSON.stringify(body), auth: false,
    }),
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export function fileUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
}

export async function downloadAuthed(path: string, filename: string, init?: RequestInit): Promise<void> {
  const tokenModule = await import("@/lib/api");
  const token = tokenModule.getAccessToken();
  const res = await fetch(`${BACKEND}/api${path}`, {
    ...init,
    headers: {
      ...(init?.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
  });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = await res.json(); } catch { /* */ }
    throw new ApiError(res.status, "Download failed", detail);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const ITEM_CATEGORIES: ItemCategory[] = [
  "Dresses", "Tops", "Bottoms", "Outerwear", "Suiting",
  "Knitwear", "Shoes", "Bags", "Accessories", "Jewelry", "Other",
];
export const CONDITIONS: Condition[] = [
  "New with tags", "Excellent", "Good", "Fair", "Needs repair", "Archive",
];
export const ACQUISITION_TYPES: AcquisitionType[] = [
  "Owned", "On loan", "Gifted", "Pulled for consideration", "Archive piece",
];
export const SEASONS: Season[] = ["SS", "FW", "Resort", "Pre-Fall", "Seasonless"];
export const LOCATION_TYPES: LocationType[] = [
  "Rack", "Bin", "Archive", "Client residence", "Storage unit", "On set", "Other",
];
export const MOVE_REASONS: MoveReason[] = [
  "Pull for fitting", "Shoot prep", "Return",
  "Storage rotation", "Repair", "Other",
];
export const OCCASIONS: Occasion[] = [
  "Press", "Red carpet", "Editorial", "Fitting", "Tour",
  "Daywear", "Evening", "Resort", "Other",
];
export const BORROWER_TYPES: BorrowerType[] = [
  "Client", "Talent", "Shoot", "Stylist", "Other",
];
export const RETURN_CONDITIONS: ReturnCondition[] = [
  "As sent", "Minor wear", "Needs cleaning", "Needs repair", "Damaged",
];

export function formatMoney(value?: number | null): string {
  if (!value && value !== 0) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

export function timeAgo(iso: string): string {
  const date = new Date(iso);
  const ms = Date.now() - date.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 14) return `${d}d ago`;
  return date.toLocaleDateString();
}

export function dueDescriptor(dueIso: string): { label: string; tone: "ok" | "warn" | "bad" } {
  const due = new Date(dueIso).getTime();
  const ms = due - Date.now();
  const days = Math.round(ms / 86400000);
  if (ms < 0) {
    const overdueDays = Math.abs(days);
    return {
      label: overdueDays === 0 ? "Overdue today" : `${overdueDays}d overdue`,
      tone: "bad",
    };
  }
  if (days <= 2) return { label: days === 0 ? "Due today" : `Due in ${days}d`, tone: "warn" };
  if (days <= 7) return { label: `Due in ${days}d`, tone: "warn" };
  return { label: `Due in ${days}d`, tone: "ok" };
}

// ===== Phase 5: RBAC, admin, audit, reports, imports =====

export type WorkspaceRole = "owner" | "admin" | "member";

export interface TeamMember {
  id: string;
  user_id: string | null;
  email: string;
  full_name?: string | null;
  role: WorkspaceRole;
  status: "active" | "pending";
  invited_by?: string | null;
  invited_at?: string | null;
  accepted_at?: string | null;
}

export interface AdminOverview {
  total_items: number;
  total_clients: number;
  total_locations: number;
  total_outfits: number;
  active_loans: number;
  overdue_loans: number;
  inventory_value_retail: number;
  inventory_value_insured: number;
  members_active: number;
  members_pending: number;
  trial_days_left: number | null;
  workspace_name?: string | null;
  workspace_plan?: string | null;
  data_integrity: {
    items_without_photo: number;
    items_without_location: number;
    loans_overdue_over_14d: number;
    clients_without_items: number;
  };
}

export interface AuditEntry {
  id: string;
  workspace_id: string;
  actor_user_id?: string | null;
  actor_email?: string | null;
  action: string;
  action_label: string;
  resource_type?: string | null;
  resource_id?: string | null;
  resource_label?: string | null;
  metadata: Record<string, unknown>;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at: string;
}

export interface AuditListResponse {
  rows: AuditEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface RecentLoginEntry { at: string; ip?: string | null; user_agent?: string | null }

export const teamApi = {
  list: () => apiFetch<TeamMember[]>("/team/members"),
  invite: (body: { email: string; role: "admin" | "member" }) =>
    apiFetch<TeamMember>("/team/invite", { method: "POST", body: JSON.stringify(body) }),
  inviteInfo: (token: string) =>
    apiFetch<{
      email: string; role: string;
      workspace_name?: string;
      inviter_name?: string | null;
      user_exists?: boolean;
    }>(
      `/team/invite/${encodeURIComponent(token)}/info`, { auth: false }
    ),
  acceptInvite: (token: string, body: { full_name?: string; password?: string } = {}) =>
    apiFetch<{ workspace_id: string; role: string }>(
      `/team/invite/${encodeURIComponent(token)}/accept`,
      { method: "POST", body: JSON.stringify(body) }
    ),
  changeRole: (memberId: string, role: "admin" | "member") =>
    apiFetch<TeamMember>(`/team/members/${memberId}`, {
      method: "PATCH", body: JSON.stringify({ role }),
    }),
  remove: (memberId: string) =>
    apiFetch<{ removed: true }>(`/team/members/${memberId}`, { method: "DELETE" }),
  transferOwnership: (body: { to_user_id: string; password: string }) =>
    apiFetch<{ transferred: true }>("/team/transfer-ownership", {
      method: "POST", body: JSON.stringify(body),
    }),
};

export const adminApi = {
  overview: () => apiFetch<AdminOverview>("/admin/overview"),
  recentActivity: () => apiFetch<AuditEntry[]>("/admin/recent-activity"),
};

export const auditApi = {
  list: (params: {
    action?: string[]; actor_user_id?: string;
    resource_type?: string; q?: string;
    from?: string; to?: string;
    page?: number; page_size?: number;
  } = {}) => {
    const sp = new URLSearchParams();
    params.action?.forEach((a) => sp.append("action", a));
    if (params.actor_user_id) sp.set("actor_user_id", params.actor_user_id);
    if (params.resource_type) sp.set("resource_type", params.resource_type);
    if (params.q) sp.set("q", params.q);
    if (params.from) sp.set("from", params.from);
    if (params.to) sp.set("to", params.to);
    if (params.page) sp.set("page", String(params.page));
    if (params.page_size) sp.set("page_size", String(params.page_size));
    const s = sp.toString();
    return apiFetch<AuditListResponse>(`/audit-log${s ? `?${s}` : ""}`);
  },
};

export const reportsApi = {
  inventoryValue: () => apiFetch<any>("/reports/inventory-value"),
  usage: (days: number = 90) => apiFetch<any>(`/reports/usage?days=${days}`),
  conditionHealth: () => apiFetch<any>("/reports/condition-health"),
  seasonCoverage: () => apiFetch<any>("/reports/season-coverage"),
  loansThroughput: () => apiFetch<any>("/reports/loans-throughput"),
  clientBreakdown: (id: string) => apiFetch<any>(`/reports/client-breakdown/${id}`),
};

export const profileApi = {
  changePassword: (body: { current_password: string; new_password: string }) =>
    apiFetch<{ ok: true }>("/profile/password", { method: "POST", body: JSON.stringify(body) }),
  requestEmailChange: (new_email: string) =>
    apiFetch<{ ok: true; pending_email: string }>("/profile/email", {
      method: "POST", body: JSON.stringify({ new_email }),
    }),
  cancelEmailChange: () =>
    apiFetch<{ ok: true }>("/profile/email/cancel", { method: "POST" }),
  recentLogins: () => apiFetch<{
    last_login_at?: string | null;
    last_login_ip?: string | null;
    last_login_user_agent?: string | null;
    recent_logins: RecentLoginEntry[];
  }>("/profile/recent-logins"),
};

export const workspaceApi = {
  get: () => apiFetch<{
    id: string; name?: string | null; logo_url?: string | null;
    plan?: string | null; default_currency?: string;
    trial_ends_at?: string | null; owner_user_id?: string;
  }>("/workspace"),
  update: (body: { name?: string }) =>
    apiFetch<any>("/workspace", { method: "PATCH", body: JSON.stringify(body) }),
  delete: (body: { workspace_name_confirmation: string; password: string }) =>
    apiFetch<{ ok: true }>("/workspace", { method: "DELETE", body: JSON.stringify(body) }),
  uploadLogo: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<{ logo_url: string }>("/workspace/logo", { method: "POST", body: fd });
  },
  removeLogo: () =>
    apiFetch<{ ok: true; logo_url: null }>("/workspace/logo", { method: "DELETE" }),
};

export interface ImportOptions {
  auto_create_clients?: boolean;
  auto_create_locations?: boolean;
  skip_invalid?: boolean;
  dry_run?: boolean;
  default_location?: string;
  default_brand?: string;
  default_category?: string;
  tab_as_brand?: boolean;
}

export const importsApi = {
  preview: async (file: File, options: ImportOptions = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("options", JSON.stringify(options));
    return apiFetch<any>("/imports/items/preview", { method: "POST", body: fd });
  },
  commit: async (file: File, options: ImportOptions) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("options", JSON.stringify(options));
    return apiFetch<any>("/imports/items/commit", { method: "POST", body: fd });
  },
  photoJob: async (jobId: string) =>
    apiFetch<{
      id: string; status: string; total: number; processed: number;
      imported: number; failures: number; errors: any[];
    }>(`/imports/items/photo-job/${jobId}`),
  uploadPhotosZip: async (
    file: File,
    onProgress?: (pct: number) => void,
  ) => {
    // Chunk threshold — files at/under this go in one request; larger files
    // are sliced so no single request trips the proxy's ~100MB body limit.
    const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB
    if (file.size <= CHUNK_SIZE) {
      const fd = new FormData();
      fd.append("file", file);
      onProgress?.(10);
      const res = await apiFetch<any>("/imports/items/photos-zip", { method: "POST", body: fd });
      onProgress?.(100);
      return res;
    }
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < total; i++) {
      const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", String(i));
      fd.append("file", blob, `part-${i}`);
      await apiFetch<any>("/imports/items/photos-zip/chunk", { method: "POST", body: fd });
      onProgress?.(Math.round(((i + 1) / total) * 90));
    }
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("total_chunks", String(total));
    const res = await apiFetch<any>("/imports/items/photos-zip/finalize", { method: "POST", body: fd });
    onProgress?.(100);
    return res;
  },
  zipPhotoJob: async (jobId: string) =>
    apiFetch<{
      id: string; status: string; total: number; processed: number;
      attached: number; items_updated: number; unmatched: number; unmatched_names: string[];
    }>(`/imports/items/photos-zip/job/${jobId}`),
  // Upload individual photo files (no ZIP) in small safe batches, matched by
  // filename. Returns aggregate counts and the names that didn't match.
  uploadPhotoFiles: async (
    files: File[],
    onProgress?: (done: number, total: number, attached: number, unmatched: string[]) => void,
  ) => {
    const MAX_BATCH_FILES = 8;
    const MAX_BATCH_BYTES = 20 * 1024 * 1024; // 20MB per request
    const batches: File[][] = [];
    let cur: File[] = [];
    let curSize = 0;
    for (const f of files) {
      if (cur.length && (cur.length >= MAX_BATCH_FILES || curSize + f.size > MAX_BATCH_BYTES)) {
        batches.push(cur); cur = []; curSize = 0;
      }
      cur.push(f); curSize += f.size;
    }
    if (cur.length) batches.push(cur);

    let attached = 0;
    const unmatched: string[] = [];
    let done = 0;
    onProgress?.(0, files.length, 0, []);
    for (const b of batches) {
      const fd = new FormData();
      b.forEach((f) => fd.append("files", f));
      const res = await apiFetch<any>("/imports/items/photos-files", { method: "POST", body: fd });
      attached += res.attached || 0;
      (res.unmatched || []).forEach((u: string) => unmatched.push(u));
      done += b.length;
      onProgress?.(done, files.length, attached, unmatched.slice());
    }
    return { attached, unmatched, total: files.length };
  },
};

export const ROLE_LABELS: Record<WorkspaceRole, string> = {
  owner: "Owner", admin: "Admin", member: "Member",
};
